import React, { useEffect, useState } from "react";
import { useStyles } from "./styles";
import PropTypes from "prop-types";
import {
  Grid,
  Button,
  Drawer,
  IconButton,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Link,
} from "@material-ui/core";
import ActivityTable from "./ActivityTable";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FilterListIcon from "@material-ui/icons/FilterList";
import Title from "../Title";
import useHubContext from "../../hooks/useHubContext";
import useHasPermissions from "../../hooks/useHasPermissions";
import AccessHolderForm from "../Forms/AccessHolder";
import {
  ACTIVITY_CHANGE_TOPIC,
  TRANSIENT_TYPES,
  ACTIVITY_TYPES,
  BACKOUT_ACTIVITY_TYPES,
  VALID_ACCESS,
  SOFT_ANTIPASSBACK,
  USE_ACTIVITY_DATE_AS_EXIT_DATE,
  DEVICE_MODE,
  ENTITY_TYPE,
} from "../../constants/index.js";
import ActivityFilters from "../Filters/ActivityFilters";
import { shallowEqual, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { isEmpty } from "lodash";
import apiClient from "../../auth/apiClient";
import ActivityService from "../../services/ParkingActivityService";
import { useEnqueueSnackbar } from "../../hooks/useEnqueueSnackbar";
import clsx from "clsx";
import useCancellationToken from "../../hooks/useCancellationToken";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useCoreEntityContext } from "../../hooks/useCoreEntitySlice";
import { FindEntity } from "../../state/slices/entities";
import { selectEntityById } from "../../state/slices/CoreEntity";
import { useFeatureFlag } from "../../hooks/useFeatureFlags";

const activityService = new ActivityService(apiClient);

export const Activity = ({ entityId, hideTitle, accordion, tz, supportMultiEntities }) => {
  const classes = useStyles();
  const { portalHub, Connected: PortalHubConnected } = useHubContext();
  const useCoreEntitySlice = useCoreEntityContext();
  const [open, setOpen] = React.useState(false);
  const [accessData, setAccessData] = useState({});
  const [activityRows, setActivityRows] = useState([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { hasPermissions } = useHasPermissions();
  const viewActivity = hasPermissions(["dashboard.activity.view"]);
  const RevTranReportPermission = hasPermissions(["RevenueTransaction"]);
  const AccHolderReportPermission = hasPermissions(["AccessholderActivity"]);
  const isLPREnabled = useFeatureFlag('LPR');

  const facilityID = useSelector(
    (state) => useCoreEntitySlice ? state.coreEntities?.ContextID : state.entities?.ContextID,
    shallowEqual
  );

  const entityType = useSelector((state) => {
    const entity = useCoreEntitySlice ? selectEntityById(state, facilityID) : FindEntity(state.entities?.EntityList ?? [], facilityID);
    return entity?.typeid;
  }, shallowEqual);

  const isValetAreaEntity = entityType === ENTITY_TYPE.ValetArea;
  const activityFilters = useSelector((state) => state.activity, shallowEqual);
  const history = useHistory();
  const enqueueSnackbar = useEnqueueSnackbar();
  const { execute: executeParkingActivityQuery } = useCancellationToken({
    func: getParkingActivity,
    errHandleFunc: () =>
      enqueueSnackbar("Failed to load activity", {
        variant: "error",
        tag: "FailedToLoadActivity",
      }),
  });
  const facilityGroupID = useSelector((state) => state.entityScope?.facilityGroupId);
  const [scopeEntityIDs, setScopeEntityIDs] = useState([]);
  const scopeList = useSelector(
    (state) => state.entityScope?.selected, shallowEqual
  ) ?? [];

  useEffect(() => {
    var idList = [];
    if (facilityGroupID && supportMultiEntities) {
      idList = scopeList?.map((entity) => { return entity.id; });
    } else {
      idList.push(entityId);
    }
    setScopeEntityIDs(idList);
  }, [facilityGroupID, scopeList, entityId]);

  useEffect(() => {
    executeParkingActivityQuery({
      credentialType: activityFilters.CredentialType,
      activityType: activityFilters.ActivityType,
      entityIds: scopeEntityIDs,
      isvaletArea: isValetAreaEntity,
    });
  }, [scopeEntityIDs, activityFilters.CredentialType, activityFilters.ActivityType]);

  useEffect(() => {
    if (!viewActivity) return;

    let topics = scopeEntityIDs?.map((id) => {
      return id && scopeList?.some(facility => facility.id === id)
        ? `${id}`
        : `${id}_${ACTIVITY_CHANGE_TOPIC}`;
    });

    topics.forEach((topic) => {
      portalHub.subscribe(topic, async (message) => {
        let activity;
        let isUpdate = false;
        try {
          const parsed = JSON.parse(message);
          activity = parsed.Activity;
          isUpdate = parsed.IsUpdate ?? parsed.isUpdate ?? false;
          if (!activity || activity == null) throw "Invalid Activity";
        } catch {
          var data = JSON.parse(message);
          const inner = JSON.parse(data.Message ?? data.message);
          activity = inner.Activity;
          isUpdate = inner.IsUpdate ?? inner.isUpdate ?? false;
        }

        if (activity) {
          addActivityRow(activity, isUpdate);
        }
      });
    });

    return () => {
      topics.forEach((topic) => {
        portalHub.unsubscribe(topic);
      });
    };
  }, [PortalHubConnected, viewActivity, scopeEntityIDs]);

  async function getParkingActivity({
    entityIds,
    activityType,
    credentialType,
    isvaletArea,
    cancelToken,
  }) {

    const response = isvaletArea
      ? await activityService.getParkingActivityForValetAreas(
        cancelToken,
        entityIds,
        activityType,
        credentialType
      )
      : await activityService.getParingActivityForFacilities(
        cancelToken,
        entityIds,
        activityType,
        credentialType
      );

    parseMessages(response.data);
  }

  const handleReportClick = (reportName) => {
    history.push(`/reports/${reportName}`);
  };

  const toggleFilters = () => {
    setFiltersOpen(!filtersOpen);
  };

  const handleDrawerOpen = (isAccessHolderActive, accessHolderID) => {
    if (isAccessHolderActive) {
      setAccessData(
        <AccessHolderForm
          accessHolderID={accessHolderID}
          facilityID={facilityID}
          onDelete={handleDrawerClose}
        />
      );
      setOpen(true);
    } else {
      if (isAccessHolderActive != null) {
        enqueueSnackbar("Access holder deleted", {
          variant: "error",
          tag: "AccessholderDeleted",
        });
      }
    }
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const addActivityRow = (activity, isUpdate = false) => {
    let newActivity = parseMessage(activity);
    setActivityRows((rows) => {
      // Existing LostTicket-at-Exit special case: drop any prior row for the same credential.
      if (
        newActivity.result?.toLowerCase() ===
        ACTIVITY_TYPES.LostTicket.toLowerCase() &&
        newActivity.deviceMode?.toLowerCase() === DEVICE_MODE.EXIT.toLowerCase()
      ) {
        rows = rows.filter(
          (a) => a.credentialNumber != newActivity.credentialNumber
        );
      }

      const existingIndex =
        newActivity.id != null
          ? rows.findIndex((r) => r.id === newActivity.id)
          : -1;

      // Update for a row we are still showing: update in place, keep its position.
      if (isUpdate && existingIndex !== -1) {
        const next = [...rows];
        next[existingIndex] = newActivity;
        return next;
      }

      // Update for a row that has scrolled off the live view: drop it. The DB link is
      // already persisted, so a manual refresh will surface the plate.
      if (isUpdate) {
        return rows;
      }

      // Brand-new activity: prepend newest-first and enforce the row cap.
      if (rows.length >= 25) rows.pop();
      rows.unshift(newActivity);
      return [...rows];
    });
  };

  const getExitDate = (activityType, activityDate) => {
    return activityType === ACTIVITY_TYPES.Exit ? formatDate(activityDate) : "";
  };

  const formatDate = (date) => {
    return isEmpty(date)
      ? ""
      : moment
        .utc(date)
        .tz(tz)
        ?.format("MM/DD/YYYY hh:mm:ss a");
  };

  const parseMessages = (rows) => {
    const tempActivity = [];
    rows.forEach((value) => {
      tempActivity.push(parseMessage(value));
    });
    setActivityRows(tempActivity);
  };

  const getActivityDescription = (description, deviceMode, credentialType) => {
    if (
      deviceMode?.toLowerCase() === DEVICE_MODE.EXIT.toLowerCase() &&
      USE_ACTIVITY_DATE_AS_EXIT_DATE.indexOf(credentialType?.toLowerCase()) !==
      -1 &&
      description?.toLowerCase() === ACTIVITY_TYPES.Payment.toLowerCase()
    )
      return ACTIVITY_TYPES.Exit;
    return description;
  };

  //If activity has a secondary credential, display the credential type as "Valet Ticket"
  //Skip IssueTicket tickets that have secondardy credentials
  const determineCredentialTypeDisplayed = (message, secondary = null) => {
    const resultDescription =
      message.ResultDescription ?? message.resultDescription;
    const credentialTypeID =
      message.CredentialTypeID ?? message.credentialTypeID;
    if (secondary &&
      resultDescription != null && resultDescription == "Credential Not Found" &&
      credentialTypeID != null && credentialTypeID == 6) return "Barcode";
    if (secondary &&
      resultDescription != null && (resultDescription == "Credential Not Found" || resultDescription == "Offline Not Found") &&
      credentialTypeID != null && credentialTypeID == 7) return "Prox";
    if (secondary) {
      if (credentialTypeID == 13) return "TicketMaster";
      if (credentialTypeID == 12) return "Valet Ticket";
      return "Ticket";
    }
    return message.CredentialType ?? message.credentialType;
  };

  //If activity has a secondary credential, display the secondary credential along with the credential reference
  //Skip IssueTicket tickets that have secondardy credentials
  const determineCredentialNumberDisplayed = (message, secondary = null) => {
    let originalRef =
      message.CredentialReference ?? message.credentialReference;
    const resultDescription =
      message.ResultDescription ?? message.resultDescription;
    const credentialTypeID =
      message.CredentialTypeID ?? message.credentialTypeID;

    if (resultDescription != null && (resultDescription == "Credential Not Found" || resultDescription == "Offline Not Found"))
      return secondary == '' ? originalRef : secondary;
    if (secondary) {
      if (credentialTypeID == 13) // TicketMaster
        return secondary;
      if (credentialTypeID == 12) // Valet
        return `${originalRef?.length > 11 ? originalRef.slice(0, 11) : originalRef
          } (Valet ${secondary})`;
    }
    return originalRef;
  };

  const determineLicensePlateDisplayed = (message) => {
    let licensePlate = message.LicensePlate ?? message.licensePlate;
    
    if (licensePlate === 'No Read') {
      const credType = message.credentialType ?? message.CredentialType;
      
      if (credType === "License Plate" && 
        (message.resultCode === 'HardAntiPassBack' || message.ResultCode === 4) &&
        (message.credentialReference ?? message.CredentialReference)) {
          licensePlate = message.credentialReference ?? message.CredentialReference;
      }
    }
      
    return licensePlate;
  };

  const parseMessage = (message) => {
    const secondary =
      message.SecondaryCredential ?? message.secondaryCredential;
    const credentialType = determineCredentialTypeDisplayed(message, secondary);
    const isTransient =
      TRANSIENT_TYPES.indexOf(credentialType?.toLowerCase()) !== -1;
    const displayEndDate =
      USE_ACTIVITY_DATE_AS_EXIT_DATE.indexOf(credentialType?.toLowerCase()) !==
      -1;
    const name =
      (message.FirstName ?? message.firstName ?? "") +
      " " +
      (message.LastName ?? message.lastName ?? "");
    const id = message.ParkingActivityID ?? message.parkingActivityID;
    const credentialNumber = determineCredentialNumberDisplayed(
      message,
      secondary
    );
    const deviceName = message.EntityName ?? message.entityName;
    const activityDate = message.ActivityDate ?? message.activityDate;
    const resultDescription =
      message.ResultDescription ?? message.resultDescription;
    const parentEntityID = message.ParentEntityID ?? message.parentEntityID;
    const accessHolderID = message.AccessHolderID ?? message.accessHolderID;
    const isAccessHolderActive =
      message.IsAccessHolderActive ?? message.isAccessHolderActive;
    const deviceMode = message.DeviceMode ?? message.deviceMode;
    const activityType = getActivityDescription(
      message.ActivityDescription ?? message.activityDescription,
      deviceMode,
      credentialType
    );
    const hasResult =
      resultDescription != null &&
      resultDescription?.toLowerCase() != "unset" &&
      resultDescription.toLowerCase() != "valid access";
    const facilityName = message.NearestFacilityName ?? message.nearestFacilityName;
    const credential_SubTypeId = message.credential_SubTypeId ?? message.Credential_SubTypeId;
    const licensePlate = determineLicensePlateDisplayed(message);
    var newActivity = {
      id: id,
      activityType: activityType,
      credentialNumber: credentialNumber,
      credentialType: credentialType,
      name: name,
      deviceName:
        activityType.toLowerCase() === "mobile payment"
          ? "Mobile Pay"
          : activityType.toLowerCase() === "roaming cashier"
            ? "Roaming Cashier"
            : deviceName,
      activityDate: formatDate(activityDate),
      result: activityType + (hasResult ? " - " + resultDescription : ""),
      parentEntityID: parentEntityID,
      accessHolderID: accessHolderID,
      isAccessHolderActive: isAccessHolderActive,
      highlight: retrieveHighlightColor(activityType, resultDescription),
      deviceMode: deviceMode,
      nearestFacilityName: facilityName,
      credential_SubTypeId,
      licensePlate: licensePlate
    };
    return newActivity;
  };

  const retrieveHighlightColor = (type, description) => {
    if (
      BACKOUT_ACTIVITY_TYPES.indexOf(type) != -1 ||
      (description != null &&
        description?.toLowerCase() != "unset" &&
        description != VALID_ACCESS)
    ) {
      return description?.toLowerCase() == SOFT_ANTIPASSBACK.toLowerCase()
        ? "warningHighlight"
        : "errorHighlight";
    }
    return "";
  };

  if (!viewActivity) return <></>;

  if (accordion) {
    return (
      <React.Fragment>
        <Accordion
          defaultExpanded={true}
          className={clsx("activity-panel", classes.activityAccordion)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon className={clsx("expand-button")} />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
          >
            <Grid item xs={11}>
              {!hideTitle && <Title cssSelecto="title">Activity</Title>}
            </Grid>
          </AccordionSummary>

          <>
            <Grid container>
              <Grid item xs={12}>
                <ActivityFilters isValetAreaEntity={isValetAreaEntity} />
              </Grid>
            </Grid>

            <AccordionDetails>
              <ActivityTable
                filteredRows={activityRows}
                handleDrawerOpen={handleDrawerOpen}
                showFacility={facilityGroupID && scopeList.length > 1}
                showLicensePlate={isLPREnabled}
              />
            </AccordionDetails>
            <Grid container className={clsx(classes.reportLinks)}>
              <Grid item xs={12}>
                {RevTranReportPermission && (
                  <Link
                    color="primary"
                    data-id="RevenueTransactionLink"
                    onClick={() => handleReportClick("RevenueTransaction")}
                    className={clsx(
                      "revenue-transaction",
                      "report-link",
                      classes.pointer
                    )}
                  >
                    Revenue Transaction Report
                  </Link>
                )}
                {AccHolderReportPermission && (
                  <Link
                    color="primary"
                    style={{ paddingLeft: "20px" }}
                    data-id="AccessHolderActivityLink"
                    onClick={() => handleReportClick("AccessholderActivity")}
                    className={clsx(
                      "access-holder-activity",
                      "report-link",
                      classes.pointer
                    )}
                  >
                    Access Holder Activity Report
                  </Link>
                )}
              </Grid>
            </Grid>
            <Drawer
              className={clsx(classes.drawer, "access-holder-details-drawer")}
              anchor="right"
              open={open}
              classes={{
                paper: classes.drawerPaper,
                root: classes.drawer,
              }}
            >
              <Button
                onClick={handleDrawerClose}
                className={clsx("close-access-holder-drawer")}
              >
                Close
              </Button>
              <div style={{ padding: "20px" }}>{accessData}</div>
            </Drawer>
          </>
        </Accordion>
      </React.Fragment>
    );
  } else
    return (
      <React.Fragment>
        <Grid item xs={11}>
          {!hideTitle && <Title>Activity</Title>}
        </Grid>

        <>
          <Grid container>
            <Grid item xs={11}></Grid>
            <Grid item xs={1}>
              <IconButton
                data-id="pageable-filter-button"
                color="primary"
                onClick={toggleFilters}
                className={clsx(classes.filterIcon)}
              >
                <FilterListIcon fontSize="large" className={classes.icon} />
              </IconButton>
            </Grid>
          </Grid>
          <Collapse in={filtersOpen} unmountOnExit>
            <ActivityFilters isValetAreaEntity={isValetAreaEntity} />
          </Collapse>
          <ActivityTable
            filteredRows={activityRows}
            handleDrawerOpen={handleDrawerOpen}
            showFacility={false}
            showLicensePlate={false}
          />
          <Drawer
            className={clsx(classes.drawer)}
            anchor="right"
            open={open}
            classes={{
              paper: classes.drawerPaper,
              root: classes.drawer,
            }}
          >
            <Button onClick={handleDrawerClose}>Close</Button>
            <div style={{ padding: "20px" }}>{accessData}</div>
          </Drawer>
        </>
      </React.Fragment>
    );
};

Activity.defaultProps = {
  entityId: undefined,
  hideTitle: false,
  accordion: false,
  supportMultiEntities: PropTypes.bool,
};
Activity.propTypes = {
  entityData: PropTypes.object,
  hideTitle: PropTypes.bool,
  accordion: PropTypes.bool,
  supportMultiEntities: PropTypes.bool,
};

export default Activity;
